import { chatServiceColor } from './chatServiceColor';
import { SERVICE } from '../constants/service';
import { BDSColor } from '../tokens/color';

import type { ThemeOptions } from '@mui/material';

type ThemeColorWithService = {
  role: string;
  sub_description?: string;
  bubblechat: { light: string; dark: string };
  tingle: { light: string; dark: string };
};

type ThemeColor = {
  role: string;
  sub_description?: string;
  light: string;
  dark: string;
};

type RecursiveTheme<T> = {
  [K in keyof T]: T[K] extends { role: string } ? ThemeColor : T[K] extends object ? RecursiveTheme<T[K]> : T[K];
};

const getColorByService = (serviceName: 'bubblechat' | 'tingle'): RecursiveTheme<typeof chatServiceColor> => {
  const isThemeColorWithService = (val: unknown): val is ThemeColorWithService =>
    val !== null && typeof val === 'object' && 'role' in val && 'bubblechat' in val && 'tingle' in val;

  const extractTheme = <T extends object>(obj: T): RecursiveTheme<T> => {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key as keyof T];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (isThemeColorWithService(value)) {
          acc[key as keyof T] = {
            role: value.role,
            sub_description: value.sub_description ?? '',
            light: value[serviceName].light,
            dark: value[serviceName].dark,
          } as RecursiveTheme<T>[keyof T];
        } else {
          acc[key as keyof T] = extractTheme(value) as RecursiveTheme<T>[keyof T];
        }
      } else {
        acc[key as keyof T] = value as RecursiveTheme<T>[keyof T];
      }

      return acc;
    }, {} as RecursiveTheme<T>);
  };

  return extractTheme(chatServiceColor);
};

const bubblechatColor = getColorByService('bubblechat');
const tingleColor = getColorByService('tingle');

export interface ThemeConfigType {
  theme?: string;
}

const greyPalette = {
  50: BDSColor.neutral[5],
  100: BDSColor.neutral[10],
  200: BDSColor.neutral[15],
  300: BDSColor.neutral[20],
  400: BDSColor.neutral[30],
  500: BDSColor.neutral[38],
  600: BDSColor.neutral[45],
  700: BDSColor.neutral[55],
  800: BDSColor.neutral[60],
  900: BDSColor.neutral[70],
  A100: BDSColor.neutral[10],
  A200: BDSColor.neutral[15],
  A400: BDSColor.neutral[30],
  A700: BDSColor.neutral[55],
};

export const muiBaseOptions: ThemeOptions = {
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
        },
        '#root': {
          height: '100%',
        },
        '#nprogress .bar': {
          zIndex: '2000 !important',
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 600,
    },
    fontFamily: 'Pretendard, sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    overline: {
      fontWeight: 600,
    },
  },
};

export const muiThemesOptions = {
  [SERVICE.BUBBLECHAT]: {
    // MUI 테마 + 커스텀 palette (BDSColor) 병합
    // MuiThemeProvider가 EmotionThemeProvider를 덮어쓰기 때문에
    // styled 컴포넌트에서 theme.palette 접근을 위해 BDSColor 추가
    palette: {
      grey: greyPalette,
      // MUI 팔레트는 리터럴 색이어야 한다(MUI alpha()/darken() 등이 CSS 변수를 파싱 못 함).
      // static.primary/secondary는 v2 토큰(var(--v2-*))에 alias돼 있으므로 여기선 v2 브랜드 리터럴을 직접 쓴다.
      // (실제 리스킨은 emotion theme.color.brand.* var로 정상 동작 — 아래는 MUI 컴포넌트 내부용 폴백.)
      primary: {
        contrastText: '#ffffff',
        main: '#00E0FF',
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#AE85FD',
      },
      ...bubblechatColor,
      ...BDSColor,
    },
  },
  [SERVICE.TINGLE]: {
    palette: {
      grey: greyPalette,
      // MUI 팔레트 리터럴(v2 브랜드) — CSS 변수 alias 회피(위 주석 참고)
      primary: {
        contrastText: '#ffffff',
        main: '#F95388',
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#FFF820',
      },
      ...tingleColor,
      ...BDSColor,
    },
  },
};
